import { useNode, useEditor } from "@craftjs/core";
import React, { ReactNode, useRef } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Typography,
  Button as MuiButton,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { containerMaxWidths, justifyContentMap, alignItemsMap, ContainerMaxWidth, shadows } from "../../config/styleConfig";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { ImageUpload } from "../ui/ImageUpload";

export interface ContainerProps {
  children?: ReactNode;
  background?: string;
  padding?: number;
  paddingX?: number;
  paddingY?: number;
  paddingLeft?: number;
  paddingRight?: number;
  margin?: number;
  marginTop?: number;
  maxWidth?: "full" | "screen-xl" | "screen-lg" | "screen-md";
  minHeight?: number;
  flexDirection?: "row" | "column";
  justifyContent?: "start" | "center" | "end" | "between" | "around";
  alignItems?: "start" | "center" | "end" | "stretch" | "baseline";
  gap?: number;
  className?: string;
  // Mobile layout behavior when flexDirection is 'row'
  mobileLayout?: "stack" | "scroll" | "wrap";
  // New properties
  borderRadius?: number;
  boxShadow?: "none" | "sm" | "md" | "lg" | "xl";
  overflow?: "visible" | "hidden" | "auto";
  width?: string;
  backgroundImage?: string;
  backgroundSize?: "cover" | "contain" | "auto";
  backgroundPosition?: string;
  disableResponsive?: boolean;
  order?: number;
  mobileOrder?: number;
  hideOnMobile?: boolean;
  hideOnDesktop?: boolean;
  mobileAlignItems?: "start" | "center" | "end" | "stretch";
  mobilePaddingX?: number;
  mobilePaddingY?: number;
  mobileBackgroundPosition?: string;
  sectionId?: string;
}

// Using shared config from styleConfig.ts
// containerMaxWidths, justifyContentMap, alignItemsMap are imported

export const Container = ({
  children,
  background = "transparent",
  padding = 16,
  paddingX,
  paddingY,
  paddingLeft,
  paddingRight,
  margin = 0,
  marginTop,
  maxWidth = "full",
  minHeight = 0,
  flexDirection = "column",
  justifyContent = "start",
  alignItems = "stretch",
  gap = 0,
  mobileLayout = "stack",
  borderRadius = 0,
  boxShadow = "none",
  overflow = "visible",
  width,
  backgroundImage,
  backgroundSize = "cover",
  backgroundPosition = "center",
  disableResponsive = false,
  order,
  mobileOrder,
  hideOnMobile = false,
  hideOnDesktop = false,
  mobileAlignItems,
  mobilePaddingX,
  mobilePaddingY,
  mobileBackgroundPosition,
  sectionId,
}: ContainerProps) => {
  const {
    connectors: { connect, drag },
    selected,
    childCount,
  } = useNode((state) => ({
    selected: state.events.selected,
    childCount: (state.data.nodes || []).length,
  }));

  const { inEditor } = useEditor((state) => ({ inEditor: true }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { containerWidth } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  // Calculate mobile styles based on container width (works in preview mode)
  // IMPORTANT: Only apply row flex styles when we KNOW it's desktop (containerWidth > 1024)
  // This prevents initial render issues where containerWidth is 0
  // When disableResponsive is true, always treat as desktop
  const isConfirmedDesktop = disableResponsive || containerWidth > 1024;
  const shouldApplyMobileStyles = flexDirection === "row" && !isConfirmedDesktop;

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      // Section ID for anchor navigation
      {...(sectionId ? { id: sectionId, "data-section-id": sectionId } : {})}
      // Data attributes for parent-driven responsive hide/show
      {...(hideOnMobile ? { "data-hide-on-mobile": "true" } : {})}
      {...(hideOnDesktop ? { "data-hide-on-desktop": "true" } : {})}
      {...(mobileAlignItems ? { "data-mobile-align-items": mobileAlignItems } : {})}
      sx={{
        display: "flex",
        // Use column layout when: (1) flexDirection is column, OR (2) not confirmed desktop and mobileLayout is stack
        flexDirection:
          flexDirection === "column"
            ? "column"
            : isConfirmedDesktop
              ? "row"
              : mobileLayout === "stack"
                ? "column"
                : "row",
        flexWrap: (flexDirection === "row" && mobileLayout === "wrap") ? "wrap" : "nowrap",
        maxWidth: containerMaxWidths[(maxWidth || "full") as ContainerMaxWidth] || "100%",
        justifyContent: justifyContentMap[justifyContent || "start"] || justifyContent,
        alignItems: alignItemsMap[alignItems || "stretch"] || alignItems,
        // Use 'background' for gradients, 'bgcolor' for solid colors
        ...(background?.startsWith("linear-gradient") || background?.startsWith("radial-gradient")
          ? { background }
          : { bgcolor: background }),
        paddingTop: `${(!isConfirmedDesktop && mobilePaddingY !== undefined) ? mobilePaddingY : (paddingY ?? padding)}px`,
        paddingBottom: `${inEditor && childCount === 0
          ? Math.max((!isConfirmedDesktop && mobilePaddingY !== undefined) ? mobilePaddingY : (paddingY ?? padding), 40)
          : ((!isConfirmedDesktop && mobilePaddingY !== undefined) ? mobilePaddingY : (paddingY ?? padding))}px`,
        paddingLeft: `${(!isConfirmedDesktop && mobilePaddingX !== undefined) ? mobilePaddingX : (paddingLeft ?? paddingX ?? padding)}px`,
        paddingRight: `${(!isConfirmedDesktop && mobilePaddingX !== undefined) ? mobilePaddingX : (paddingRight ?? paddingX ?? padding)}px`,
        marginTop: `${marginTop ?? margin}px`,
        marginBottom: `${margin}px`,
        marginLeft: (width && width !== "100%" && width !== "auto" && !margin) ? "auto" : `${margin}px`,
        marginRight: (width && width !== "100%" && width !== "auto" && !margin) ? "auto" : `${margin}px`,
        minHeight: minHeight || "auto",
        gap: `${gap}px`,
        // New properties
        borderRadius: borderRadius != null ? `${borderRadius}px` : undefined,
        boxShadow: shadows[boxShadow as keyof typeof shadows] || "none",
        overflow,
        // Width and flex override (no !important so parent mobile stack can override)
        ...(width ? { width, flex: "0 0 auto" } : {}),
        // Order support for responsive reordering
        ...(order !== undefined || mobileOrder !== undefined
          ? { order: isConfirmedDesktop ? (order ?? 0) : (mobileOrder ?? order ?? 0) }
          : {}),
        // Background image
        ...(backgroundImage
          ? {
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize,
              backgroundPosition: (!isConfirmedDesktop && mobileBackgroundPosition) ? mobileBackgroundPosition : backgroundPosition,
              backgroundRepeat: "no-repeat",
            }
          : {}),
        "& > *": {
          // CONFIRMED desktop row mode: children share space equally
          // Use flex: 1 1 auto (not 0%) to prevent children from collapsing to 0 width
          // Skip when container is fit-content (width: auto) to avoid spreading children apart
          ...(flexDirection === "row" && isConfirmedDesktop && width !== "auto"
            ? {
                flex: "1 1 auto",
                maxWidth: "100%",
              }
            : {}),
          // Mobile/tablet/unknown width: children take full width with natural height
          // This is the SAFE default that works regardless of initial containerWidth
          ...(shouldApplyMobileStyles && mobileLayout === "stack"
            ? {
                width: "100% !important",
                maxWidth: "100%",
                flex: "0 0 auto !important",
              }
            : {}),
        },
        // Parent-driven responsive hide/show using data attributes
        // Parent knows its own containerWidth reliably, so it controls children visibility
        ...(isConfirmedDesktop
          ? { '& > [data-hide-on-desktop="true"]': { display: "none !important" } }
          : {}),
        ...(!isConfirmedDesktop
          ? {
              '& > [data-hide-on-mobile="true"]': { display: "none !important" },
              '& > [data-mobile-align-items="center"]': { alignItems: "center !important" },
              '& > [data-mobile-align-items="start"]': { alignItems: "flex-start !important" },
              '& > [data-mobile-align-items="end"]': { alignItems: "flex-end !important" },
              '& > [data-mobile-align-items="stretch"]': { alignItems: "stretch !important" },
            }
          : {}),
        // Scroll mode specific styles
        ...(shouldApplyMobileStyles && mobileLayout === "scroll"
          ? {
              overflowX: "auto",
              overflowY: "hidden",
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              "& > *": {
                flexShrink: 0,
                scrollSnapAlign: "start",
                minWidth: "280px",
                maxWidth: "85vw",
              },
            }
          : {}),
        ...(selected && selectedOutlineSx),
      }}
    >
      {children}
    </Box>
  );
};

const SettingsGroup = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
    <Box sx={{ px: 1.5, py: 1, bgcolor: "#f9fafb", borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>{children}</Box>
  </Box>
);

export const ContainerSettings = () => {
  const {
    actions: { setProp },
    background,
    padding,
    paddingX,
    paddingY,
    paddingLeft,
    paddingRight,
    margin,
    marginTop,
    maxWidth,
    minHeight,
    flexDirection,
    justifyContent,
    alignItems,
    gap,
    mobileLayout,
    borderRadius,
    boxShadow,
    overflow,
    width,
    backgroundImage,
    backgroundSize,
    backgroundPosition,
    disableResponsive,
    hideOnMobile,
    hideOnDesktop,
    mobilePaddingX,
    mobilePaddingY,
    mobileAlignItems,
    mobileBackgroundPosition,
    sectionId,
  } = useNode((node) => ({
    background: node.data.props.background,
    padding: node.data.props.padding,
    paddingX: node.data.props.paddingX,
    paddingY: node.data.props.paddingY,
    paddingLeft: node.data.props.paddingLeft,
    paddingRight: node.data.props.paddingRight,
    margin: node.data.props.margin,
    marginTop: node.data.props.marginTop,
    maxWidth: node.data.props.maxWidth,
    minHeight: node.data.props.minHeight,
    flexDirection: node.data.props.flexDirection,
    justifyContent: node.data.props.justifyContent,
    alignItems: node.data.props.alignItems,
    gap: node.data.props.gap,
    mobileLayout: node.data.props.mobileLayout,
    borderRadius: node.data.props.borderRadius,
    boxShadow: node.data.props.boxShadow,
    overflow: node.data.props.overflow,
    width: node.data.props.width,
    backgroundImage: node.data.props.backgroundImage,
    backgroundSize: node.data.props.backgroundSize,
    backgroundPosition: node.data.props.backgroundPosition,
    disableResponsive: node.data.props.disableResponsive,
    hideOnMobile: node.data.props.hideOnMobile,
    hideOnDesktop: node.data.props.hideOnDesktop,
    mobilePaddingX: node.data.props.mobilePaddingX,
    mobilePaddingY: node.data.props.mobilePaddingY,
    mobileAlignItems: node.data.props.mobileAlignItems,
    mobileBackgroundPosition: node.data.props.mobileBackgroundPosition,
    sectionId: node.data.props.sectionId,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Layout */}
      <SettingsGroup title="Layout">
        <FormControl size="small" fullWidth>
          <InputLabel>Direction</InputLabel>
          <Select value={flexDirection} label="Direction"
            onChange={(e) => setProp((props: ContainerProps) => (props.flexDirection = e.target.value as "row" | "column"))}
          >
            <MenuItem value="column">Column</MenuItem>
            <MenuItem value="row">Row</MenuItem>
          </Select>
        </FormControl>

        {flexDirection === "row" && (
          <FormControl size="small" fullWidth>
            <InputLabel>Mobile Layout</InputLabel>
            <Select value={mobileLayout || "stack"} label="Mobile Layout"
              onChange={(e) => setProp((props: ContainerProps) => (props.mobileLayout = e.target.value as ContainerProps["mobileLayout"]))}
            >
              <MenuItem value="stack">Stack (แนวตั้ง)</MenuItem>
              <MenuItem value="scroll">Scroll (เลื่อนซ้ายขวา)</MenuItem>
              <MenuItem value="wrap">Wrap (ขึ้นบรรทัดใหม่)</MenuItem>
            </Select>
          </FormControl>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Justify</InputLabel>
            <Select value={justifyContent} label="Justify"
              onChange={(e) => setProp((props: ContainerProps) => (props.justifyContent = e.target.value as ContainerProps["justifyContent"]))}
            >
              <MenuItem value="start">Start</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="end">End</MenuItem>
              <MenuItem value="between">Between</MenuItem>
              <MenuItem value="around">Around</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Align</InputLabel>
            <Select value={alignItems} label="Align"
              onChange={(e) => setProp((props: ContainerProps) => (props.alignItems = e.target.value as ContainerProps["alignItems"]))}
            >
              <MenuItem value="stretch">Stretch</MenuItem>
              <MenuItem value="start">Start</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="end">End</MenuItem>
              <MenuItem value="baseline">Baseline</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body2" gutterBottom>Gap: {gap}px</Typography>
          <Slider value={gap} onChange={(_, v) => setProp((props: ContainerProps) => (props.gap = v as number))} min={0} max={100} size="small" valueLabelDisplay="auto" />
        </Box>

        <FormControl size="small" fullWidth>
          <InputLabel>Max Width</InputLabel>
          <Select value={maxWidth} label="Max Width"
            onChange={(e) => setProp((props: ContainerProps) => (props.maxWidth = e.target.value as ContainerProps["maxWidth"]))}
          >
            <MenuItem value="full">Full</MenuItem>
            <MenuItem value="screen-xl">XL (1280px)</MenuItem>
            <MenuItem value="screen-lg">LG (1024px)</MenuItem>
            <MenuItem value="screen-md">MD (768px)</MenuItem>
          </Select>
        </FormControl>

        <TextField label="Width" value={width || ""} placeholder="Auto"
          onChange={(e) => setProp((props: ContainerProps) => (props.width = e.target.value || undefined))}
          size="small" fullWidth helperText='e.g. "100%", "50%", "300px"'
        />
      </SettingsGroup>

      {/* Spacing */}
      <SettingsGroup title="Spacing">
        <Box>
          <Typography variant="body2" gutterBottom>Padding: {padding}px</Typography>
          <Slider value={padding} onChange={(_, v) => setProp((props: ContainerProps) => (props.padding = v as number))} min={0} max={100} size="small" valueLabelDisplay="auto" />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField label="Padding X" type="number" value={paddingX ?? ""} placeholder="Auto" size="small"
            onChange={(e) => setProp((props: ContainerProps) => (props.paddingX = e.target.value ? parseInt(e.target.value) : undefined))}
          />
          <TextField label="Padding Y" type="number" value={paddingY ?? ""} placeholder="Auto" size="small"
            onChange={(e) => setProp((props: ContainerProps) => (props.paddingY = e.target.value ? parseInt(e.target.value) : undefined))}
          />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField label="Padding Left" type="number" value={paddingLeft ?? ""} placeholder="Auto" size="small"
            onChange={(e) => setProp((props: ContainerProps) => (props.paddingLeft = e.target.value ? parseInt(e.target.value) : undefined))}
          />
          <TextField label="Padding Right" type="number" value={paddingRight ?? ""} placeholder="Auto" size="small"
            onChange={(e) => setProp((props: ContainerProps) => (props.paddingRight = e.target.value ? parseInt(e.target.value) : undefined))}
          />
        </Box>
        <Box>
          <Typography variant="body2" gutterBottom>Margin: {margin}px</Typography>
          <Slider value={margin} onChange={(_, v) => setProp((props: ContainerProps) => (props.margin = v as number))} min={0} max={100} size="small" valueLabelDisplay="auto" />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
          <TextField label="Margin Top" type="number" value={marginTop ?? ""} placeholder="Auto" size="small"
            onChange={(e) => setProp((props: ContainerProps) => (props.marginTop = e.target.value ? parseInt(e.target.value) : undefined))}
          />
        </Box>
        <Box>
          <Typography variant="body2" gutterBottom>Min Height: {minHeight}px</Typography>
          <Slider value={minHeight} onChange={(_, v) => setProp((props: ContainerProps) => (props.minHeight = v as number))} min={0} max={800} size="small" valueLabelDisplay="auto" />
        </Box>
      </SettingsGroup>

      {/* Style */}
      <SettingsGroup title="Style">
        <Box>
          <Typography variant="body2" gutterBottom>Background</Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box component="input" type="color" value={background === "transparent" ? "#ffffff" : background}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProp((props: ContainerProps) => (props.background = e.target.value))}
              sx={{ width: 48, height: 40, borderRadius: 1, border: "1px solid", borderColor: "divider", cursor: "pointer" }}
            />
            <MuiButton size="small" onClick={() => setProp((props: ContainerProps) => (props.background = "transparent"))} sx={{ textTransform: "none" }}>
              Transparent
            </MuiButton>
          </Box>
        </Box>

        <Box>
          <Typography variant="body2" gutterBottom>Border Radius: {borderRadius ?? 0}px</Typography>
          <Slider value={borderRadius ?? 0} onChange={(_, v) => setProp((props: ContainerProps) => (props.borderRadius = v as number))} min={0} max={50} size="small" valueLabelDisplay="auto" />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Shadow</InputLabel>
            <Select value={boxShadow || "none"} label="Shadow"
              onChange={(e) => setProp((props: ContainerProps) => (props.boxShadow = e.target.value as ContainerProps["boxShadow"]))}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="sm">Small</MenuItem>
              <MenuItem value="md">Medium</MenuItem>
              <MenuItem value="lg">Large</MenuItem>
              <MenuItem value="xl">XL</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Overflow</InputLabel>
            <Select value={overflow || "visible"} label="Overflow"
              onChange={(e) => setProp((props: ContainerProps) => (props.overflow = e.target.value as ContainerProps["overflow"]))}
            >
              <MenuItem value="visible">Visible</MenuItem>
              <MenuItem value="hidden">Hidden</MenuItem>
              <MenuItem value="auto">Auto</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </SettingsGroup>

      {/* Background Image */}
      <SettingsGroup title="Background Image">
        <ImageUpload label="Image" value={backgroundImage || ""}
          onChange={(url) => setProp((props: ContainerProps) => (props.backgroundImage = url || undefined))}
        />
        {backgroundImage && (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel>Size</InputLabel>
              <Select value={backgroundSize || "cover"} label="Size"
                onChange={(e) => setProp((props: ContainerProps) => (props.backgroundSize = e.target.value as ContainerProps["backgroundSize"]))}
              >
                <MenuItem value="cover">Cover</MenuItem>
                <MenuItem value="contain">Contain</MenuItem>
                <MenuItem value="auto">Auto</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Position" value={backgroundPosition || "center"} placeholder="center" size="small" fullWidth
              onChange={(e) => setProp((props: ContainerProps) => (props.backgroundPosition = e.target.value || "center"))}
            />
            <TextField label="Mobile Position" value={mobileBackgroundPosition || ""} placeholder="ตาม Desktop" size="small" fullWidth
              onChange={(e) => setProp((props: ContainerProps) => (props.mobileBackgroundPosition = e.target.value || undefined))}
            />
          </>
        )}
      </SettingsGroup>

      {/* Mobile & Responsive */}
      <SettingsGroup title="Responsive">
        <FormControlLabel
          control={<Checkbox checked={disableResponsive || false} onChange={(e) => setProp((props: ContainerProps) => (props.disableResponsive = e.target.checked))} size="small" />}
          label={<Typography variant="body2">ไม่ปรับตาม Responsive</Typography>}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Mobile Align</InputLabel>
          <Select value={mobileAlignItems || ""} label="Mobile Align"
            onChange={(e) => setProp((props: ContainerProps) => (props.mobileAlignItems = (e.target.value || undefined) as ContainerProps["mobileAlignItems"]))}
          >
            <MenuItem value="">ตาม Desktop</MenuItem>
            <MenuItem value="start">Start</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="end">End</MenuItem>
            <MenuItem value="stretch">Stretch</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField label="Mobile Padding X" type="number" value={mobilePaddingX ?? ""} placeholder="Auto" size="small"
            onChange={(e) => setProp((props: ContainerProps) => (props.mobilePaddingX = e.target.value ? parseInt(e.target.value) : undefined))}
          />
          <TextField label="Mobile Padding Y" type="number" value={mobilePaddingY ?? ""} placeholder="Auto" size="small"
            onChange={(e) => setProp((props: ContainerProps) => (props.mobilePaddingY = e.target.value ? parseInt(e.target.value) : undefined))}
          />
        </Box>
        <Box>
          <Typography variant="body2" gutterBottom>การแสดงผล</Typography>
          <RadioGroup
            value={hideOnDesktop ? "mobile" : hideOnMobile ? "desktop" : "all"}
            onChange={(e) => {
              const v = e.target.value;
              setProp((props: ContainerProps) => { props.hideOnMobile = v === "desktop"; props.hideOnDesktop = v === "mobile"; });
            }}
          >
            <FormControlLabel value="all" control={<Radio size="small" />} label={<Typography variant="body2">ทุกอุปกรณ์</Typography>} />
            <FormControlLabel value="desktop" control={<Radio size="small" />} label={<Typography variant="body2">เฉพาะ Desktop</Typography>} />
            <FormControlLabel value="mobile" control={<Radio size="small" />} label={<Typography variant="body2">เฉพาะ Mobile</Typography>} />
          </RadioGroup>
        </Box>
      </SettingsGroup>

      {/* Advanced */}
      <SettingsGroup title="Advanced">
        <TextField label="Section ID" value={sectionId || ""} placeholder="e.g. hero, features" size="small"
          onChange={(e) => setProp((props: ContainerProps) => (props.sectionId = e.target.value || undefined))}
          helperText="สำหรับ anchor link (#id)"
        />
      </SettingsGroup>
    </Box>
  );
};

Container.craft = {
  displayName: "Container",
  isCanvas: true, // Allow elements to be reordered within container
  props: {
    background: "transparent",
    padding: 16,
    paddingX: undefined,
    paddingY: undefined,
    paddingLeft: undefined,
    paddingRight: undefined,
    margin: 0,
    marginTop: undefined,
    maxWidth: "full",
    minHeight: 0,
    flexDirection: "column",
    justifyContent: "start",
    alignItems: "stretch",
    gap: 0,
    mobileLayout: "stack",
    borderRadius: 0,
    boxShadow: "none",
    overflow: "visible",
    width: undefined,
    backgroundImage: undefined,
    backgroundSize: "cover",
    backgroundPosition: "center",
    disableResponsive: false,
    hideOnMobile: false,
    hideOnDesktop: false,
    order: undefined,
    mobileOrder: undefined,
    mobileAlignItems: undefined,
    mobilePaddingX: undefined,
    mobilePaddingY: undefined,
    mobileBackgroundPosition: undefined,
    sectionId: undefined,
  },
  rules: {
    canDrag: () => true,
    // Allow reordering within same container, block accidental drops to parent
    canMoveIn: (incomingNodes: any[], currentNode: any) => {
      const currentChildren = currentNode.data?.nodes || [];

      for (const node of incomingNodes) {
        // Always allow if element is already a child (reordering)
        if (currentChildren.includes(node.id)) {
          return true;
        }
        // Block if element's parent is a child of this container (accidental parent drop)
        if (currentChildren.includes(node.data?.parent)) {
          return false;
        }
      }
      return true;
    },
  },
  related: {
    settings: ContainerSettings,
  },
};

// Root container that allows dropping
export const RootContainer = ({ children }: { children?: ReactNode }) => {
  const {
    connectors: { connect },
  } = useNode();

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(ref);
      }}
      sx={{
        minHeight: "calc(100vh - 200px)",
        width: "100%",
        // Ensure the drop zone is visible even when empty
        position: "relative",
      }}
    >
      {children}
    </Box>
  );
};

RootContainer.craft = {
  displayName: "Root",
  isCanvas: true,
  props: {},
  rules: {
    canDrag: () => false,
    canMoveIn: () => true,
  },
};
